import React, { useRef, useState } from 'react';

import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import { Box } from '@mui/material';

import { COLOURS } from 'utils/color';

interface UploadViewProps {
  onFile: (file: File) => void;
  error: string | null;
}

const UploadView: React.FC<UploadViewProps> = ({ onFile, error }) => {
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
  };

  return (
    <Box
      style={{
        width: '100%',
        display: 'flex',
        minHeight: '100vh',
        background: '#F0F2F5',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif",
      }}>
      <div style={{ width: 520, textAlign: 'center' }}>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              marginBottom: 12,
              alignItems: 'center',
              display: 'inline-flex',
              justifyContent: 'center',
              background: COLOURS.headerBg,
            }}>
            <ViewTimelineOutlinedIcon sx={{ color: '#FFF' }} />
          </div>

          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLOURS.headerBg, margin: 0 }}>
            Planner to Timeline Gantt Chart
          </h1>
          <p style={{ fontSize: 13, color: COLOURS.textLight, marginTop: 4 }}>
            Upload your Excel export to generate the project timeline
          </p>
        </div>

        <div
          role="button"
          tabIndex={0}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDrop={handleDrop}
          onDragLeave={() => setDragging(false)}
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => {
            // Trigger the file input when the user presses Enter or Space
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault(); // Prevents the default page scroll when pressing Space
              fileRef.current?.click();
            }
          }}
          style={{
            cursor: 'pointer',
            borderRadius: 10,
            padding: '48px 32px',
            transition: 'all 0.15s ease',
            border: `2px dashed ${dragging ? COLOURS.headerBg : COLOURS.border}`,
            background: dragging ? 'rgba(31,56,100,0.04)' : '#FFF',
          }}>
          <p style={{ fontSize: 14, color: COLOURS.text, fontWeight: 600, margin: '0 0 4px' }}>
            Drop your .xlsx file here
          </p>
          <p style={{ fontSize: 12, color: COLOURS.textLight, margin: 0 }}>or click to browse</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleChange} style={{ display: 'none' }} />
        </div>

        {error && (
          <div
            style={{
              fontSize: 12,
              marginTop: 16,
              borderRadius: 6,
              textAlign: 'left',
              color: '#991B1B',
              padding: '10px 16px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
            }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <div
          style={{
            marginTop: 24,
            borderRadius: 8,
            textAlign: 'left',
            background: '#FFF',
            padding: '14px 18px',
            border: `1px solid ${COLOURS.gridLine}`,
          }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: COLOURS.text, margin: '0 0 6px' }}>
            Expected MS Planner Export Format:
          </p>
          <div style={{ fontSize: 10.5, color: COLOURS.textLight, lineHeight: 1.7 }}>
            <div>Row 1: Project name | Row 2: Plan owner | Row 3: Start | Row 4: Finish | Row 5: Duration</div>
            <div style={{ marginTop: 4 }}>Header row containing `Task number`, then task data rows with columns:</div>
            <div style={{ marginTop: 2, fontFamily: 'monospace', fontSize: 10 }}>
              A: Task # &nbsp;B: Outline &nbsp;D: Name &nbsp;H: Start &nbsp;I: Finish &nbsp;J: Duration &nbsp;M: Depends
              on
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default UploadView;
